import { useMemo, useState } from 'react';
import { Building2, Lock, Plus, Trash2, User, Users } from '../../../icons';
import { Badge, Button, SearchInput } from '../../../components/shared';
import type { BoardDto, BoardGroup } from '../../../api/boards';
import { tBoards } from '../../../lib/boards-i18n';
import { BoardMark } from '../BoardMarks';
import { filterBoardsByName, groupBoards } from './board-list-model';

/**
 * 게시판 트리 카드 — 결정 Q1 「콘텐츠 안 트리 카드」 (시안 v1 `.bdcard` · 정본 제안 4 `BOARDTREE`).
 *
 * 도메인 셸 좌측 메뉴가 아니다 — 본문 격자 안의 카드이고 폭은 `--layout-board-tree-w`(272, 관제 Q-F).
 *
 * ★ 구획은 회사 → 부서 → 팀 → 내. **빈 구획은 그리지 않되 「내 게시판」만은 선다** — 「만든 게시판이
 *   없습니다 · 게시판 만들기」가 만들기의 입구이기 때문이다(시안).
 * ★ 찾기 칸은 늘 선다(시안). 찾는 중에는 빈 구획을 모두 감춘다.
 * ★ 안 읽음 배지는 `primary` 알약이다 — 「외치는 알림」(count 빨강)이 아니다(현행 `BoardSideList` 와 같은 판단).
 * ★ 비공개는 자물쇠 **그림 + 읽어 주는 이름**(색만으로 뜻을 만들지 않는다).
 */

const GROUP_ICON: Record<BoardGroup, typeof Users> = {
  company: Building2,
  division: Users,
  team: Users,
  mine: User,
};

export interface BoardTreeCardProps {
  boards: BoardDto[];
  activeId: string | null;
  canCreate: boolean;
  /** 서버가 휴지통을 모르면(404/405) 감춘다 — 누를 수 없는 단추를 남기지 않는다. */
  showTrash: boolean;
  onSelect: (boardId: string) => void;
  onCreate: () => void;
  onOpenTrash: () => void;
  /**
   * 어디에 놓이는가. 기본 `card` = 종전 그대로(본문 격자 안의 카드).
   * `shell` = 도메인 셸 좌측 메뉴(2026-09-17 사용자 지시) — 카드 껍데기(테두리·그림자·둥근 모서리)를
   * 벗고 좌측 패널 높이를 그대로 채운다. **기본값이 종전 동작이라 카드 쪽은 회귀가 없다.**
   */
  variant?: 'card' | 'shell';
}

export function BoardTreeCard({
  boards, activeId, canCreate, showTrash, onSelect, onCreate, onOpenTrash, variant = 'card',
}: BoardTreeCardProps) {
  const [term, setTerm] = useState('');
  const searching = term.trim().length > 0;
  const groups = useMemo(() => groupBoards(filterBoardsByName(boards, term)), [boards, term]);
  const shown = groups.reduce((n, g) => n + g.boards.length, 0);

  return (
    <aside className={variant === 'shell' ? 'bv1-tree bv1-tree--shell' : 'bv1-card bv1-tree'}>
      <div className="bv1-tree__head">
        <h2 className="bv1-card__title">{tBoards('boards.side.title')}</h2>
        <span className="bv1-card__sub bv1-num">{boards.length}</span>
      </div>

      <SearchInput
        wrapperClassName="bv1-tree__find"
        value={term}
        placeholder={tBoards('boards.side.find')}
        aria-label={tBoards('boards.side.find')}
        onChange={(e) => setTerm(e.target.value)}
        onClear={() => setTerm('')}
      />

      <nav className="bv1-tree__nav" aria-label={tBoards('boards.nav_aria')}>
        {searching && shown === 0 && <p className="bv1-tree__empty">{tBoards('boards.side.find_empty')}</p>}
        {groups.map(({ group, boards: rows }) => {
          if (rows.length === 0 && (group !== 'mine' || searching)) return null;
          const Icon = GROUP_ICON[group];
          return (
            <section key={group} className="bv1-tree__group">
              <h3 className="bv1-tree__label">
                <Icon aria-hidden className="bv1-tree__gicon" />
                {tBoards(`boards.group.${group}` as never)}
                <span className="bv1-tree__gcount bv1-num">{rows.length}</span>
              </h3>
              {rows.length === 0 ? (
                <p className="bv1-tree__none">
                  {tBoards('boards.v1.tree.none_mine')}
                  {canCreate && (
                    <>
                      {' · '}
                      <button type="button" className="bv1-tree__link" onClick={onCreate}>
                        {tBoards('boards.btn.create')}
                      </button>
                    </>
                  )}
                </p>
              ) : (
                <ul className="bv1-tree__list">
                  {rows.map((b) => {
                    const selected = activeId === b.id;
                    const unread = b.unreadCount ?? 0;
                    return (
                      <li key={b.id}>
                        <button
                          type="button"
                          className={[
                            'bv1-tree__node',
                            group === 'company' ? 'bv1-tree__node--company' : '',
                            selected ? 'is-selected' : '',
                          ].filter(Boolean).join(' ')}
                          aria-current={selected ? 'page' : undefined}
                          onClick={() => onSelect(b.id)}
                        >
                          <span className="bv1-tree__dot" aria-hidden />
                          <span className="bv1-tree__name" title={b.name}>{b.name}</span>
                          {b.systemKey === 'company-default' && <BoardMark kind="default-board" />}
                          {b.boardType === 'FEED' && <BoardMark kind="feed" />}
                          {b.isPrivate && (
                            <Lock className="bv1-tree__lock" role="img" aria-label={tBoards('boards.v1.tree.private')} />
                          )}
                          {unread > 0 && (
                            <Badge variant="primary" size="sm">
                              <span title={tBoards('boards.wide.unread_badge', { n: unread })}>
                                {unread > 99 ? '99+' : unread}
                              </span>
                            </Badge>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </nav>

      <div className="bv1-tree__foot">
        {showTrash && (
          <Button variant="secondary" size="compact" iconLeft={<Trash2 aria-hidden />} onClick={onOpenTrash}>
            {tBoards('boards.side.trash')}
          </Button>
        )}
        <Button
          variant="primary"
          size="compact"
          iconLeft={<Plus aria-hidden />}
          disabled={!canCreate}
          {...(!canCreate ? { title: tBoards('boards.side.create_disabled') } : {})}
          onClick={onCreate}
        >
          {tBoards('boards.btn.create')}
        </Button>
      </div>
    </aside>
  );
}
